// Libraries
import { useAppDispatch } from "#src/store/hooks";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  LoaderFunction,
  useLoaderData,
  useParams,
  generatePath,
} from "react-router-dom";
import i18next from "i18next";

// Resources
import { pathname } from "#src/config/pathname";

// General
import { updateWorkspaceList } from "#src/store/slices/workspace";
import patterns from "#src/config/patterns";

// Components
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import LoadingState from "#src/common/system/LoadingState";
import NoResultsState from "#src/common/states/NoResultsState";
import { createToast } from "#src/common/system/toasts";
import { ContentSection } from "#src/layouts/content";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";
import WorkspaceDangerZone from "#src/components/workspace/details/DangerZone";
import WorkspaceLogo from "#src/components/workspace/details/Logo";

import WorkspaceAdditionalInformation, {
  type IWorkspaceAdditionalForm,
} from "#src/components/workspace/details/AdditionalInformation";
import WorkspaceBasicInformation, {
  type IWorkspaceFormBasic,
} from "#src/components/workspace/details/BasicInformation";

// API-related
import workspaceServices, {
  type IWorkspaceItem,
} from "#src/services/workspace";

import { getPermissionFromWorkspace, isAllowedTo } from "#src/permission";
import { PermissionAction } from "#src/permission/actions";

type LoaderType = IWorkspaceItem | null;

export const workspaceDetailsLoader: LoaderFunction = async ({
  params,
}): Promise<LoaderType> => {
  const workspaceId = params.workspaceId;

  if (!workspaceId) return null;

  const workspaceDetails = await workspaceServices
    .retrieve_workspace(workspaceId)
    .then((response) => {
      return response;
    })
    .catch(apiErrorHandler);

  const allowedToRead = isAllowedTo(
    PermissionAction.WORKSPACE_SETTINGS_READ,
    getPermissionFromWorkspace(workspaceDetails)
  );

  if (!allowedToRead) throw Error("403");

  return workspaceDetails;
};

/** @todo: get data from loader instead of useEffect fetching. */
export const WorkspaceDetailsPage = () => {
  const { t } = useTranslation("workspace", { keyPrefix: "detail" });
  const { t: sidebarT } = useTranslation("layout", {
    keyPrefix: "sidebar.v2",
  });
  const { workspaceId } = useParams<"workspaceId">();

  const dispatch = useAppDispatch();

  const workspaceDetails = useLoaderData() as LoaderType;

  if (workspaceDetails === null) {
    throw Error("404");
  }

  const allowedToUpdate = isAllowedTo(
    PermissionAction.WORKSPACE_SETTINGS_UPDATE,
    getPermissionFromWorkspace(workspaceDetails)
  );
  const allowedToDelete = isAllowedTo(
    PermissionAction.WORKSPACE_SETTINGS_DELETE,
    getPermissionFromWorkspace(workspaceDetails)
  );

  // --------------- DATA ---------------

  const [color, setColor] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [callingAPI, setCallingAPI] = useState<boolean>(false);
  const [currentWorkspace, setCurrentWorkspace] =
    useState<IWorkspaceItem | null>(null);
  const [initBasicInformation, setInitBasicInformation] =
    useState<IWorkspaceFormBasic>();
  const [initAdditionalInformation, setInitAdditionalInformation] =
    useState<IWorkspaceAdditionalForm>();
  const [originBasicInformation, setOriginBasicInformation] =
    useState<IWorkspaceFormBasic>();
  const [originAdditionalInformation, setOriginAdditionalInformation] =
    useState<IWorkspaceAdditionalForm>();
  const [apiErrorDetails, setApiErrorDetails] = useState<any>(null);

  const getFieldError = useCallback(
    (fieldKey: string, formData: any) => {
      const validateRequired = (value: string) => {
        if (!value?.trim()) {
          return i18next.t("common:input.error.required");
        }
        return null;
      };
      const validateMaxLength = (value: string, max: number) => {
        if (value?.length > max) {
          return i18next.t("common:input.error.maxLengthMessage", {
            length: max,
          });
        }
        return null;
      };
      if (fieldKey) {
        let message = null;
        const keys = fieldKey.split(".");
        let value = formData;
        keys.forEach((k) => {
          value = value[k];
        });
        if (["name", "website"].includes(fieldKey)) {
          message = validateRequired(value);
        }
        if (!message) {
          if (["name", "short_name"].includes(fieldKey)) {
            message = validateMaxLength(value, 255);
          } else {
            message = validateMaxLength(value, 1024);
          }
        }
        if (!message && apiErrorDetails && apiErrorDetails[fieldKey]) {
          message = apiErrorDetails[fieldKey][0] || null;
        }
        return message;
      }
      return null;
    },
    [apiErrorDetails]
  );

  const isError = useMemo(() => {
    let error = null;
    Object.keys({ ...initBasicInformation }).some((key) => {
      error = getFieldError(key, initBasicInformation);
      if (error) return error;
    });
    return !!error;
  }, [initBasicInformation, getFieldError]);

  const showSaveButton = useMemo(() => {
    return (
      JSON.stringify(initBasicInformation) !==
        JSON.stringify(originBasicInformation) ||
      JSON.stringify(initAdditionalInformation) !==
        JSON.stringify(originAdditionalInformation) ||
      currentWorkspace?.color !== color
    );
  }, [
    originBasicInformation,
    originAdditionalInformation,
    initBasicInformation,
    initAdditionalInformation,
    color,
    currentWorkspace,
  ]);

  const initFormData = (workspace: IWorkspaceItem) => {
    const basicInformation = {
      name: workspace.name,
      email: workspace.email || "",
      short_name: workspace.short_name || "",
      phone: workspace.phone || "",
      vat_tax_code: workspace.vat_tax_code || "",
      address: workspace.address || "",
      website: workspace.website || "",
      description: workspace.description,
      country_code: workspace.country_code,
    };
    setOriginBasicInformation(basicInformation);
    setInitBasicInformation(basicInformation);

    const additionalInformation = {
      social: workspace.social,
      employee_size: workspace.employee_size || "",
      industry: workspace.industry || "",
      technologies: workspace.technologies.join(","),
    };
    setOriginAdditionalInformation(additionalInformation);
    setInitAdditionalInformation(additionalInformation);
  };

  // TODO: be aware that this will affect workspace selector
  const fetchWorkspace = useCallback(
    (workspaceId: string) => {
      setLoading(true);
      workspaceServices
        .retrieve_workspace(workspaceId)
        .then((response) => {
          initFormData(response);
          setCurrentWorkspace(response);
          setColor(response.color);
          setLoading(false);
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.loadWorkspace.fail"),
          });
        });
    },
    [t]
  );

  useEffect(() => {
    if (workspaceId) {
      fetchWorkspace(workspaceId);
    }
  }, [workspaceId, fetchWorkspace]);

  useEffect(() => {
    setApiErrorDetails(null);
  }, [initBasicInformation, initAdditionalInformation]);

  const onSaveChanges = async () => {
    if (!allowedToUpdate || !initBasicInformation || !initAdditionalInformation)
      return;

    // TODO: check valid email
    if (workspaceId) {
      setCallingAPI(true);
      if (isError) return;
      const {
        name,
        short_name,
        address,
        description,
        email,
        phone,
        vat_tax_code,
        website,
        country_code,
      } = initBasicInformation;

      const { social, industry, technologies, employee_size } =
        initAdditionalInformation;

      workspaceServices
        .update_workspace(workspaceId, {
          name,
          short_name,
          address,
          email,
          country_code,
          phone,
          vat_tax_code,
          website,
          description,
          industry,
          technologies: technologies.trim().length
            ? technologies.trim().split(",")
            : [],
          employee_size,
          social: social.filter((s) => patterns.LINK.test(s.url?.trim())),
          color: color || currentWorkspace?.color,
        })
        .then((response) => {
          fetchWorkspace(response.id);
          workspaceServices
            .list_workspaces()
            .then((response) => {
              dispatch(updateWorkspaceList(response));
            })
            .catch(apiErrorHandler);
          setCallingAPI(false);
          createToast({
            type: "success",
            message: t("notification.saveChanges.success"),
          });
          setApiErrorDetails(null);
        })
        .catch((error) => {
          setCallingAPI(false);
          if (
            error.response &&
            error.response.status === 400 &&
            error.response.data.code === "0004"
          ) {
            const details = error.response.data.details;
            setApiErrorDetails(details);
          }

          apiErrorHandler(error, {
            toastMessage: t("notification.saveChanges.fail"),
          });
        });
    }
  };

  // --------------- RENDER ---------------

  return (
    <div className="flex flex-col pt-5 pb-6 gap-5 overflow-x-hidden overflow-y-auto h-full">
      {/* 1st section (Header) */}
      <div className="flex flex-col gap-5">
        {/* Header (Breadcrumb + title) */}
        <div className="px-8 flex flex-col gap-4">
          <BreadcrumbV2
            levels={[
              {
                name: sidebarT("workspaceSettings.label"),
                path: generatePath(pathname.WORKSPACE_DETAILS, {
                  workspaceId,
                }),
              },
            ]}
            currentPage={{
              name: sidebarT("workspaceSettings.details"),
            }}
          />
          <span className="text-display-xs font-semibold text-gray-v2-900">
            {t("title")}
          </span>
        </div>
      </div>
      {/* 1st section (Header) end */}
      {workspaceId ? (
        currentWorkspace &&
        initBasicInformation &&
        initAdditionalInformation ? (
          <ContentSection className="px-0">
            <div className="grid grid-cols-3 gap-8 px-8">
              <div className="col-span-2 flex flex-col gap-5">
                <WorkspaceBasicInformation
                  isError={isError}
                  callingAPI={callingAPI}
                  showSaveButton={showSaveButton}
                  allowedToUpdate={allowedToUpdate}
                  information={initBasicInformation}
                  getFieldError={getFieldError}
                  onChangeInformation={setInitBasicInformation}
                  onSaveChanges={onSaveChanges}
                />
                <WorkspaceAdditionalInformation
                  callingAPI={callingAPI}
                  information={initAdditionalInformation}
                  allowedToUpdate={allowedToUpdate}
                  getFieldError={getFieldError}
                  onChangeInformation={setInitAdditionalInformation}
                />
                {allowedToDelete && (
                  <WorkspaceDangerZone
                    currentWorkspace={currentWorkspace}
                    workspaceId={workspaceId}
                  />
                )}
              </div>
              <WorkspaceLogo
                color={color}
                currentWorkspace={currentWorkspace}
                allowedToUpdate={allowedToUpdate}
                setColor={setColor}
                fetchWorkspace={fetchWorkspace}
              />
            </div>
          </ContentSection>
        ) : isLoading ? (
          <LoadingState />
        ) : (
          <NoResultsState />
        )
      ) : null}
    </div>
  );
};
